import { IDEState, ProjectData, TabData } from '@/types';

export interface PersistedIDEState {
    tabs: TabData[];
    activeTabId: string | null;
    selectedFile: IDEState['selectedFile'];
    selectedProject: string | null;
    fileExplorer: { isVisible: boolean };
    outputPanel: { isVisible: boolean; activeTab: string };
    recentFiles: string[];
}
const record = (value: unknown): Record<string, unknown> | null =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

export function validateStoredIDEState(value: unknown): PersistedIDEState | null {
    const data = record(value);
    if (!data) return null;
    const tabs = Array.isArray(data.tabs)
        ? data.tabs.flatMap(value => {
              const tab = record(value);
              return tab &&
                  ['id', 'fileId', 'projectId', 'title'].every(key => typeof tab[key] === 'string' && tab[key])
                  ? [
                        {
                            id: tab.id as string,
                            fileId: tab.fileId as string,
                            projectId: tab.projectId as string,
                            title: tab.title as string,
                            isActive: tab.isActive === true,
                        },
                    ]
                  : [];
          })
        : [];
    const selected = record(data.selectedFile);
    const explorer = record(data.fileExplorer);
    const output = record(data.outputPanel);
    return {
        tabs,
        activeTabId: typeof data.activeTabId === 'string' ? data.activeTabId : null,
        selectedFile:
            selected && typeof selected.fileId === 'string' && typeof selected.projectId === 'string'
                ? { fileId: selected.fileId, projectId: selected.projectId }
                : null,
        selectedProject: typeof data.selectedProject === 'string' ? data.selectedProject : null,
        fileExplorer: { isVisible: typeof explorer?.isVisible === 'boolean' ? explorer.isVisible : true },
        outputPanel: {
            isVisible: typeof output?.isVisible === 'boolean' ? output.isVisible : false,
            activeTab: 'output',
        },
        recentFiles: Array.isArray(data.recentFiles)
            ? [...new Set(data.recentFiles.filter((id): id is string => typeof id === 'string'))]
            : [],
    };
}

export function restoreIDEState(saved: PersistedIDEState | null, projects: ProjectData[]): PersistedIDEState {
    const validFile = (projectId: string, fileId: string) =>
        projects.find(project => project.id === projectId)?.files.find(file => file.id === fileId);
    const seenIds = new Set<string>();
    const seenFiles = new Set<string>();
    const tabs = (saved?.tabs || []).flatMap(tab => {
        const file = validFile(tab.projectId, tab.fileId);
        const key = `${tab.projectId}/${tab.fileId}`;
        if (!file || seenIds.has(tab.id) || seenFiles.has(key)) return [];
        seenIds.add(tab.id);
        seenFiles.add(key);
        return [{ ...tab, title: file.name }];
    });
    const activeTabId =
        saved?.activeTabId === null || !saved
            ? null
            : tabs.find(tab => tab.id === saved.activeTabId)?.id || tabs[0]?.id || null;
    const selected = saved?.selectedFile;
    const fileIds = new Set(projects.flatMap(project => project.files.map(file => file.id)));
    return {
        tabs: tabs.map(tab => ({ ...tab, isActive: tab.id === activeTabId })),
        activeTabId,
        selectedFile: selected && validFile(selected.projectId, selected.fileId) ? selected : null,
        selectedProject: projects.some(project => project.id === saved?.selectedProject)
            ? saved!.selectedProject
            : null,
        fileExplorer: saved?.fileExplorer || { isVisible: true },
        outputPanel: saved?.outputPanel || { isVisible: false, activeTab: 'output' },
        recentFiles: (saved?.recentFiles || []).filter(id => fileIds.has(id)).slice(0, 10),
    };
}
